import SaveBotton from './SaveBotton'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { useBranding } from '@/contexts/BrandingContext'
import { BrandingSettings } from '@/types/branding'
import { useState, useEffect } from 'react'

function Message({ customCopy, setCustomCopy }: { customCopy: BrandingSettings['customCopy'], setCustomCopy: (customCopy: BrandingSettings['customCopy']) => void }) {
  const { branding, updateBranding, isLoading } = useBranding()
  const [hasChanges, setHasChanges] = useState(false)

  useEffect(() => {
    setHasChanges(false) // Reset changes when component mounts
  }, [])

  const handleChange = (field: keyof BrandingSettings['customCopy'], value: string) => {
    setCustomCopy({ ...customCopy, [field]: value })
    setHasChanges(true) // Mark as changed
  }

  const handleSave = async () => {
    await updateBranding({ ...branding, customCopy })
    setHasChanges(false) // Reset changes after successful save
  }

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Column 1: AI & Onboarding */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>AI Behavior</CardTitle>
              <CardDescription>
                Define how your AI assistant communicates with customers.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>AI Phone Greeting</Label>
                <textarea
                  className="w-full p-3 border rounded-md resize-none h-24 text-sm"
                  value={customCopy.aiGreeting}
                  onChange={(e) => handleChange('aiGreeting', e.target.value)}
                  placeholder="Hi, this is {aiName}, the AI assistant. How can I help you today?"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  The first thing your AI says on a call. Use {'{'}aiName{'}'} to insert the AI's name.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Onboarding</CardTitle>
              <CardDescription>
                Customize messages for the user setup process.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Welcome Screen Title</Label>
                <Input
                  value={customCopy.welcomeTitle}
                  onChange={(e) => handleChange('welcomeTitle', e.target.value)}
                  placeholder="Your AI-Powered Scheduling Assistant"
                />
              </div>
              <div>
                <Label>Welcome Screen Subtitle</Label>
                <Input
                  value={customCopy.welcomeSubtitle}
                  onChange={(e) => handleChange('welcomeSubtitle', e.target.value)}
                  placeholder="We'll get your AI ready in about 5 minutes."
                />
              </div>
              <div>
                <Label>Setup Complete Title</Label>
                <Input
                  value={customCopy.successTitle}
                  onChange={(e) => handleChange('successTitle', e.target.value)}
                  placeholder="Your AI Assistant is Ready!"
                />
              </div>
              <div>
                <Label>Setup Complete Message</Label>
                <Input
                  value={customCopy.successMessage}
                  onChange={(e) => handleChange('successMessage', e.target.value)}
                  placeholder="You can now start using {aiName} to handle your calls."
                />
                  <p className="text-xs text-muted-foreground mt-1">
                  This message appears on the final screen of the setup flow.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Column 2: CTAs & General */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Buttons & Actions (CTA)</CardTitle>
              <CardDescription>
                Set the text for key action buttons across the application.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Welcome Screen Button</Label>
                <Input
                  value={customCopy.getStartedText}
                  onChange={(e) => handleChange('getStartedText', e.target.value)}
                  placeholder="Get Started"
                />
                  <p className="text-xs text-muted-foreground mt-1">
                  Primary button on the initial welcome screen.
                </p>
              </div>
              <div>
                <Label>Setup Complete Button</Label>
                <Input
                  value={customCopy.dashboardButtonText}
                  onChange={(e) => handleChange('dashboardButtonText', e.target.value)}
                  placeholder="Go to Dashboard"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Button on the success screen that links to the dashboard.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <SaveBotton handleSave={handleSave} isSaving={isLoading} disabled={!hasChanges} />
    </div>
  )
}

export default Message